import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, GripVertical } from 'lucide-react';

export interface OnboardingChecklistItemConfig {
  key: string;
  label: string;
  script: string;
  required: boolean;
}

interface OnboardingChecklistEditorProps {
  value: OnboardingChecklistItemConfig[];
  onChange: (items: OnboardingChecklistItemConfig[]) => void;
}

export default function OnboardingChecklistEditor({ 
  value = [], 
  onChange 
}: OnboardingChecklistEditorProps) {
  const generateKey = () => `item_${Date.now()}`;

  const handleAddItem = () => {
    onChange([
      ...value,
      { key: generateKey(), label: '', script: '', required: true }
    ]);
  };

  const handleRemoveItem = (index: number) => {
    const updated = [...value];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleUpdateItem = (index: number, field: keyof OnboardingChecklistItemConfig, newValue: string | boolean) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Checklist de Onboarding</h3>
          <p className="text-xs text-muted-foreground">
            Defina os itens que devem ser verificados durante a sessão de onboarding
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={handleAddItem}>
          <Plus className="h-4 w-4 mr-1" />
          Adicionar
        </Button>
      </div>

      {value.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-6 text-center text-muted-foreground">
          <p className="text-sm">Nenhum item no checklist</p>
          <p className="text-xs mt-1">Clique em "Adicionar" para criar itens</p>
        </div>
      ) : (
        <div className="space-y-2">
          {value.map((item, index) => (
            <div 
              key={item.key || index}
              className="p-4 border rounded-lg bg-muted/30 space-y-3"
            >
              <div className="flex items-center gap-3">
                <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0 cursor-grab" />
                
                <Input
                  value={item.label}
                  onChange={(e) => handleUpdateItem(index, 'label', e.target.value)}
                  placeholder="Título do item (ex: Revisar contrato)"
                  className="flex-1"
                />
                
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Switch
                    id={`required-${index}`}
                    checked={item.required}
                    onCheckedChange={(checked) => handleUpdateItem(index, 'required', checked)}
                  />
                  <Label htmlFor={`required-${index}`} className="text-xs whitespace-nowrap">
                    Obrigatório
                  </Label>
                </div>
                
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive flex-shrink-0"
                  onClick={() => handleRemoveItem(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="ml-7">
                <Label className="text-xs text-muted-foreground mb-1 block">
                  Script (o que falar com o cliente)
                </Label>
                <Textarea
                  value={item.script || ''}
                  onChange={(e) => handleUpdateItem(index, 'script', e.target.value)}
                  placeholder="Instruções detalhadas do que confirmar com o cliente..."
                  className="min-h-[80px] text-sm"
                />
              </div>
            </div>
          ))}
        </div>
      )}
      
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {value.length} item(ns) • {value.filter(i => i.required).length} obrigatório(s)
        </p>
      )}
    </div>
  );
}
